import { BasePage } from '@/components/ui/base-page'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useState } from 'react'
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel"
import { Button } from '@/components/ui/button'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { MediaCard } from '@/components/cards'

// Define the article type
type ArticleCategory = 'Opinion' | 'Partner Insights' | 'Investment Announcements'

interface Article {
    id: string
    slug: string
    title: string
    excerpt: string
    coverImage: string
    date: string
    author: {
        name: string
        avatar: string
    }
    category: ArticleCategory
    tags: string[]
}

// Sample articles data
const articles: Article[] = [
    {
        id: '1',
        slug: 'the-future-of-fintech',
        title: 'The Future of Fintech in 2024',
        excerpt: 'Exploring emerging trends in financial technology and what to expect in the coming year.',
        coverImage: 'https://images.unsplash.com/photo-1620714223084-8fcacc6dfd8d',
        date: 'April 12, 2024',
        author: {
            name: 'Sarah Johnson',
            avatar: 'https://randomuser.me/api/portraits/women/44.jpg'
        },
        category: 'Opinion',
        tags: ['Fintech', 'Innovation', 'Trends']
    },
    {
        id: '2',
        slug: 'sustainable-investment-strategies',
        title: 'Sustainable Investment Strategies for Long-term Growth',
        excerpt: 'How ESG factors are reshaping portfolio management and creating new opportunities.',
        coverImage: 'https://images.unsplash.com/photo-1579532537598-459ecdaf39cc',
        date: 'April 5, 2024',
        author: {
            name: 'Michael Chen',
            avatar: 'https://randomuser.me/api/portraits/men/22.jpg'
        },
        category: 'Investment Announcements',
        tags: ['ESG', 'Sustainable', 'Growth']
    },
    {
        id: '3',
        slug: 'ai-revolutionizing-venture-capital',
        title: 'How AI is Revolutionizing Venture Capital',
        excerpt: 'The impact of artificial intelligence on investment decisions and startup evaluation.',
        coverImage: 'https://images.unsplash.com/photo-1555774698-0b77e0d5fac6',
        date: 'March 28, 2024',
        author: {
            name: 'Elena Rodriguez',
            avatar: 'https://randomuser.me/api/portraits/women/29.jpg'
        },
        category: 'Partner Insights',
        tags: ['AI', 'VC', 'Technology']
    },
    {
        id: '4',
        slug: 'crypto-regulations-2024',
        title: 'Navigating Crypto Regulations in 2024',
        excerpt: 'Understanding the evolving regulatory landscape for cryptocurrency investments.',
        coverImage: 'https://images.unsplash.com/photo-1639762681057-408e52192e55',
        date: 'March 21, 2024',
        author: {
            name: 'David Park',
            avatar: 'https://randomuser.me/api/portraits/men/77.jpg'
        },
        category: 'Opinion',
        tags: ['Crypto', 'Regulation', 'Compliance']
    }
]

// Sample podcast data
const podcasts = [
    {
        id: '1',
        title: 'The Future of Investing in Digital Markets',
        description: 'Exploring the evolution of investment strategies in the age of digital assets.',
        coverImage: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=500&q=80',
        date: 'May 20, 2023',
        duration: '45 min',
        author: {
            name: 'Sarah Chen',
        },
        tags: ['Investing', 'Digital Assets'],
        href: '/podcast/future-of-investing'
    },
    {
        id: '2',
        title: 'Digital Regulation: Navigating the New Landscape',
        description: 'Understanding the regulatory frameworks emerging around digital finance.',
        coverImage: 'https://images.unsplash.com/photo-1569025690938-a00729c9e1f9?auto=format&fit=crop&w=500&q=80',
        date: 'June 2, 2023',
        duration: '38 min',
        author: {
            name: 'Michael Wong',
        },
        tags: ['Regulation', 'Digital Finance'],
        href: '/podcast/digital-regulation'
    },
    {
        id: '3',
        title: 'Crypto Winter: Lessons for Long-term Investors',
        description: 'Key takeaways from market downturns for strategic cryptocurrency investors.',
        coverImage: 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?auto=format&fit=crop&w=500&q=80',
        date: 'May 15, 2023',
        duration: '42 min',
        author: {
            name: 'Alex Fernandez',
        },
        tags: ['Crypto', 'Investment Strategy'],
        href: '/podcast/crypto-winter'
    },
    {
        id: '4',
        title: 'Venture Capital in the Digital Age',
        description: 'How venture capital is evolving to meet the needs of digital-first startups.',
        coverImage: 'https://images.unsplash.com/photo-1559589689-577aabd1db4f?auto=format&fit=crop&w=500&q=80',
        date: 'April 28, 2023',
        duration: '51 min',
        author: {
            name: 'Jessica Kim',
        },
        tags: ['VC', 'Startups'],
        href: '/podcast/venture-capital-digital-age'
    }
];

// Sample video data
const videos = [
    {
        id: '1',
        title: 'Blockchain Investing Guide: Beyond Cryptocurrencies',
        description: 'A comprehensive guide to investing in blockchain technology beyond just cryptocurrencies.',
        coverImage: 'https://images.unsplash.com/photo-1639322537228-f710d846310a?auto=format&fit=crop&w=500&q=80',
        date: 'June 15, 2023',
        duration: '18:24',
        views: '24,892',
        author: {
            name: 'Michael Rodriguez',
        },
        tags: ['Blockchain', 'Investing'],
        href: '/vid/blockchain-investing-guide'
    },
    {
        id: '2',
        title: 'Digital Asset Allocation Strategies',
        description: 'Optimal approaches to allocating digital assets within a diversified portfolio.',
        coverImage: 'https://images.unsplash.com/photo-1622630998477-20aa696ecb05?auto=format&fit=crop&w=500&q=80',
        date: 'May 23, 2023',
        duration: '14:38',
        views: '18,437',
        author: {
            name: 'Sophia Chen',
        },
        tags: ['Asset Allocation', 'Portfolio Management'],
        href: '/vid/digital-asset-allocation'
    },
    {
        id: '3',
        title: 'The Future of DeFi: Opportunities and Risks',
        description: 'Exploring the potential and challenges of decentralized finance for investors.',
        coverImage: 'https://images.unsplash.com/photo-1605792657660-596af9009e82?auto=format&fit=crop&w=500&q=80',
        date: 'June 5, 2023',
        duration: '22:15',
        views: '31,205',
        author: {
            name: 'James Wilson',
        },
        tags: ['DeFi', 'Risk Management'],
        href: '/vid/defi-opportunities-risks'
    },
    {
        id: '4',
        title: 'Navigating Crypto Market Volatility',
        description: 'Strategies for managing and benefiting from cryptocurrency market fluctuations.',
        coverImage: 'https://images.unsplash.com/photo-1621761191319-c6fb62004040?auto=format&fit=crop&w=500&q=80',
        date: 'April 18, 2023',
        duration: '16:42',
        views: '42,891',
        author: {
            name: 'Emma Taylor',
        },
        tags: ['Crypto', 'Volatility'],
        href: '/vid/crypto-volatility'
    }
];

// Article Card Component
const ArticleCard = ({ article }: { article: Article }) => {
    const [isHovering, setIsHovering] = useState(false)

    return (
        <Link to={`/article/${article.slug}`} className="block">
            <Card
                className={`h-full overflow-hidden transition-all duration-300 ${isHovering ? 'transform scale-[1.03] shadow-xl' : 'shadow-md'
                    }`}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
            >
                <div className="relative h-48 w-full overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10 transition-opacity duration-300 ${isHovering ? 'opacity-70' : 'opacity-40'
                        }`} />
                    <div
                        className={`relative w-full h-full bg-cover bg-center transition-transform duration-500 ${isHovering ? 'scale-110' : 'scale-100'
                            }`}
                        style={{ backgroundImage: `url(${article.coverImage})` }}
                    >
                        {isHovering && (
                            <div className="absolute inset-0 animate-pulse bg-white/10 z-10"></div>
                        )}
                        <div className="absolute bottom-4 left-4 z-20">
                            <span className="inline-block bg-white text-black text-xs font-semibold px-2 py-1 rounded">
                                {article.category}
                            </span>
                        </div>
                    </div>
                </div>

                <CardContent className="pt-4">
                    <div className="mb-2 flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full overflow-hidden">
                            <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${article.author.avatar})` }} />
                        </div>
                        <span className="text-sm text-gray-600">{article.author.name}</span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-sm text-gray-600">{article.date}</span>
                    </div>
                    <h3 className={`font-bold mb-2 transition-all duration-300 ${isHovering ? 'text-blue-600' : 'text-gray-900'
                        }`}>
                        {article.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">{article.excerpt}</p>
                </CardContent>

                <CardFooter className="pt-2 flex flex-wrap gap-1">
                    {article.tags.map(tag => (
                        <span
                            key={tag}
                            className={`text-xs px-2 py-1 rounded-full transition-colors duration-300 ${isHovering
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700'
                                }`}
                        >
                            {tag}
                        </span>
                    ))}
                </CardFooter>
            </Card>
        </Link>
    )
}

// Modal for showing all media items
interface MediaModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    items: any[];
    type: 'podcast' | 'video';
}

const MediaModal = ({ isOpen, onClose, title, items, type }: MediaModalProps) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-neutral-900 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-xl font-bold">{title}</h2>
                    <button onClick={onClose} className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full">
                        &times;
                    </button>
                </div>
                <div className="p-4 overflow-y-auto flex-grow">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map(item => (
                            <MediaCard
                                key={item.id}
                                id={item.id}
                                title={item.title}
                                type={type}
                                description={item.description}
                                coverImage={item.coverImage}
                                date={item.date}
                                duration={item.duration}
                                views={item.views}
                                author={item.author}
                                tags={item.tags}
                                href={item.href}
                                size="md"
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export function BlogPage2() {
    const location = useLocation();
    const isAllBlogPage = location.pathname === '/insights/blog/all';
    const [showPodcastModal, setShowPodcastModal] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);

    return (
        <BasePage
            title="Blog"
            description="Insights, opinions, and announcements from our team and partners"
        >
            {isAllBlogPage ? (
                <div className="space-y-12">
                    {/* Featured Article */}
                    <div className="relative rounded-xl overflow-hidden shadow-lg h-96">
                        <div
                            className="absolute inset-0 bg-cover bg-center"
                            style={{ backgroundImage: `url(${articles[0].coverImage})` }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white">
                                    <div
                                        className="w-full h-full bg-cover bg-center"
                                        style={{ backgroundImage: `url(${articles[0].author.avatar})` }}
                                    />
                                </div>
                                <div>
                                    <p className="text-sm font-medium">{articles[0].author.name}</p>
                                    <p className="text-xs opacity-80">{articles[0].date}</p>
                                </div>
                            </div>
                            <span className="inline-block bg-white text-black text-xs font-semibold px-3 py-1 rounded-full mb-3">
                                {articles[0].category}
                            </span>
                            <h2 className="text-3xl font-bold mb-3">{articles[0].title}</h2>
                            <p className="text-lg opacity-90 mb-4">{articles[0].excerpt}</p>
                            <Link
                                to={`/article/${articles[0].slug}`}
                                className="inline-flex items-center gap-2 text-white bg-polymath hover:bg-white px-4 py-2 rounded-lg transition-colors duration-200"
                            >
                                Read Article
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>

                    {/* Article Grid */}
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold">Latest Articles</h2>
                            <div className="flex gap-2">
                                <button className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">
                                    All
                                </button>
                                <button className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">
                                    Opinion
                                </button>
                                <button className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">
                                    Partner Insights
                                </button>
                                <button className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">
                                    Releases & Updates
                                </button>
                                <button className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">
                                    Investment Announcements
                                </button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {articles.slice(1).map(article => (
                                <ArticleCard key={article.id} article={article} />
                            ))}
                        </div>
                    </div>

                    {/* Podcast Carousel Section */}
                    <section className="py-8 border-b">
                        <div className="container">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-2xl font-bold">Latest Podcasts</h2>
                                <Button
                                    variant="ghost"
                                    className="flex items-center gap-1"
                                    onClick={() => setShowPodcastModal(true)}
                                >
                                    See all <ArrowUpRight size={16} />
                                </Button>
                            </div>
                            <Carousel className="w-full">
                                <CarouselContent>
                                    {podcasts.map((podcast) => (
                                        <CarouselItem key={podcast.id} className="sm:basis-1/2 md:basis-1/3 lg:basis-1/4">
                                            <MediaCard
                                                id={podcast.id}
                                                title={podcast.title}
                                                type="podcast"
                                                description={podcast.description}
                                                coverImage={podcast.coverImage}
                                                date={podcast.date}
                                                duration={podcast.duration}
                                                author={podcast.author}
                                                tags={podcast.tags}
                                                href={podcast.href}
                                                size="sm"
                                            />
                                        </CarouselItem>
                                    ))}
                                </CarouselContent>
                                <CarouselPrevious />
                                <CarouselNext />
                            </Carousel>
                        </div>
                    </section>

                    {/* Video Carousel Section */}
                    <section className="py-8 border-b">
                        <div className="container">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-2xl font-bold">Featured Videos</h2>
                                <Button
                                    variant="ghost"
                                    className="flex items-center gap-1"
                                    onClick={() => setShowVideoModal(true)}
                                >
                                    See all <ArrowUpRight size={16} />
                                </Button>
                            </div>
                            <Carousel className="w-full">
                                <CarouselContent>
                                    {videos.map((video) => (
                                        <CarouselItem key={video.id} className="sm:basis-1/2 md:basis-1/3 lg:basis-1/4">
                                            <MediaCard
                                                id={video.id}
                                                title={video.title}
                                                type="video"
                                                description={video.description}
                                                coverImage={video.coverImage}
                                                date={video.date}
                                                duration={video.duration}
                                                views={video.views}
                                                author={video.author}
                                                tags={video.tags}
                                                href={video.href}
                                                size="sm"
                                            />
                                        </CarouselItem>
                                    ))}
                                </CarouselContent>
                                <CarouselPrevious />
                                <CarouselNext />
                            </Carousel>
                        </div>
                    </section>

                    {/* Media modals */}
                    <MediaModal
                        isOpen={showPodcastModal}
                        onClose={() => setShowPodcastModal(false)}
                        title="All Podcasts"
                        items={podcasts}
                        type="podcast"
                    />
                    <MediaModal
                        isOpen={showVideoModal}
                        onClose={() => setShowVideoModal(false)}
                        title="All Videos"
                        items={videos}
                        type="video"
                    />
                </div>
            ) : (
                <Outlet />
            )}
        </BasePage>
    )
} 